'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

type BaseProps = React.InputHTMLAttributes<HTMLInputElement>

type EnhancedProps = BaseProps & {
  label?: React.ReactNode
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  withPasswordToggle?: boolean // when type="password", shows an eye icon to toggle visibility
}

const RawInput = React.forwardRef<HTMLInputElement, BaseProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          ' w-full rounded-md border border-input bg-background px-3 py-2.5 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground outline-none focus:outline-none focus-visible:outline-none focus:border-[hsl(var(--primary-blue))] focus-visible:border-[hsl(var(--primary-blue))] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
RawInput.displayName = 'RawInput'

const Input = React.forwardRef<HTMLInputElement, EnhancedProps>(
  (
    {
      className,
      type,
      label,
      startIcon,
      endIcon,
      withPasswordToggle,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const setRefs = (node: HTMLInputElement) => {
      inputRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref)
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
    }

    const wantsDateEnhance = type === 'date'
    const isEnhanced = Boolean(
      label || startIcon || endIcon || withPasswordToggle || wantsDateEnhance
    )

    // Backward compatibility: render plain input when not enhanced (and not a date, which we enhance by default)
    if (!isEnhanced) {
      return <RawInput ref={ref} type={type} className={className} {...props} />
    }

    const isPassword = type === 'password'
    const effectiveType =
      isPassword && withPasswordToggle
        ? showPassword
          ? 'text'
          : 'password'
        : type
    const isDate = effectiveType === 'date'

    const openNativePicker = () => {
      const el = inputRef.current
      if (!el) return
      // Try showPicker if supported (Chromium-based)
      if (typeof el.showPicker === 'function') {
        el.showPicker()
      } else {
        el.focus()
        el.click()
      }
    }

    return (
      <div className={cn('space-y-2')}>
        {label && (
          <label className='text-sm font-medium text-foreground'>{label}</label>
        )}
        <div
          className='relative'
          onClick={(e) => {
            if (isDate) {
              // Avoid double-trigger when clicking the icon button; it will handle its own click
              const target = e.target as HTMLElement
              if (target.closest('button')) return
              openNativePicker()
            }
          }}
        >
          {startIcon && (
            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground flex items-center justify-center'>
              {startIcon}
            </span>
          )}
          <RawInput
            ref={setRefs}
            type={effectiveType}
            {...props}
            className={cn(
              'eb-input',
              className,
              startIcon ? 'pl-10' : '',
              endIcon || isDate || (isPassword && withPasswordToggle)
                ? 'pr-3'
                : ''
            )}
          />
          {/* Right-aligned decorative calendar icon for date inputs */}
          {/* {isDate && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
          )} */}

          {/* End icon or password toggle (no custom date icon button) */}
          {(endIcon || (isPassword && withPasswordToggle)) && (
            <button
              type='button'
              onClick={() => {
                if (isPassword && withPasswordToggle) setShowPassword((v) => !v)
              }}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
              aria-label={
                isPassword && withPasswordToggle
                  ? showPassword
                    ? 'Hide password'
                    : 'Show password'
                  : 'Icon button'
              }
            >
              {isPassword && withPasswordToggle ? (
                showPassword ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 24'
                    className='h-4 w-4'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.11 1 12c.64-1.49 1.61-2.86 2.82-4.06M10.58 10.58a2 2 0 1 0 2.83 2.83' />
                    <path d='M1 1l22 22' />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 24'
                    className='h-4 w-4'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
                    <circle cx='12' cy='12' r='3' />
                  </svg>
                )
              ) : (
                <span className='flex items-center justify-center'>
                  {endIcon}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
